import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTopicArea } from '../api/topicAreaApi';
import { bulkCreateRawArchive, parseRawArchive, ParsedArchiveItem } from '../api/rawArchiveApi';
import { CreditSummary, getMyCredit, requestCredit } from '../api/creditApi';
import CategoryAutocomplete from '../components/UI/CategoryAutocomplete';
import { TopicArea } from '../types';
import { useAuth } from '../hooks/useAuth';
import './RawArchiveImportPage.css';

const acceptedExtensions = ['.txt', '.md'];
const AI_AGENT_STABLE_MAX_CHARS = Number(process.env.REACT_APP_AI_AGENT_STABLE_MAX_CHARS || 120000);

const isTextFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();
  return acceptedExtensions.some(extension => lowerName.endsWith(extension));
};

const normalizeTags = (value: string): string[] =>
  value
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean)
    .filter((tag, index, array) => array.indexOf(tag) === index);

const validateItem = (item: ParsedArchiveItem): string[] => {
  const errors: string[] = [];
  if (!item.title.trim()) errors.push('제목이 필요합니다.');
  if (item.title.trim().length > 200) errors.push('제목은 200자 이하이어야 합니다.');
  if (!item.description.trim()) errors.push('설명이 필요합니다.');
  if (!item.timestamp || Number.isNaN(Date.parse(item.timestamp))) errors.push('저장 가능한 날짜/시간이 필요합니다.');
  return errors;
};

const toSelectedCategoryIds = (item: ParsedArchiveItem): number[] =>
  [item.main_category1_id, item.main_category2_id, item.main_category3_id].filter(
    (id): id is number => typeof id === 'number'
  );

const RawArchiveImportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const topicAreaId = Number(id);
  const navigate = useNavigate();
  const { loading: authLoading, isLoggedIn } = useAuth();
  const [topicArea, setTopicArea] = useState<TopicArea | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [rawText, setRawText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState('');
  const [filePreview, setFilePreview] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [provider, setProvider] = useState('mock');
  const [maxItems, setMaxItems] = useState(50);
  const [items, setItems] = useState<ParsedArchiveItem[]>([]);
  const [documentWarnings, setDocumentWarnings] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);
  const [parseElapsedSeconds, setParseElapsedSeconds] = useState(0);
  const [creating, setCreating] = useState(false);
  const [creditSummary, setCreditSummary] = useState<CreditSummary | null>(null);
  const [displayCreditBalance, setDisplayCreditBalance] = useState<number | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditRequesting, setCreditRequesting] = useState(false);
  const [forceCreditRequestVisible, setForceCreditRequestVisible] = useState(false);
  const creditAnimationRef = useRef<number | null>(null);

  useEffect(() => {
    const loadTopicArea = async () => {
      if (!Number.isInteger(topicAreaId) || topicAreaId < 1) {
        setPageError('잘못된 주제영역 ID입니다.');
        setPageLoading(false);
        return;
      }

      try {
        setPageLoading(true);
        const data = await getTopicArea(topicAreaId);
        setTopicArea(data);
      } catch (error: any) {
        setPageError(error.response?.data?.error || '주제영역 정보를 불러오지 못했습니다.');
      } finally {
        setPageLoading(false);
      }
    };

    loadTopicArea();
  }, [topicAreaId]);

  useEffect(() => {
    if (!parsing) {
      setParseElapsedSeconds(0);
      return;
    }

    const timer = window.setInterval(() => {
      setParseElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [parsing]);

  useEffect(() => {
    return () => {
      if (creditAnimationRef.current) {
        window.clearInterval(creditAnimationRef.current);
      }
    };
  }, []);

  const refreshCreditSummary = async () => {
    try {
      setCreditLoading(true);
      const summary = await getMyCredit();
      setCreditSummary(summary);
      setDisplayCreditBalance(prev => prev === null ? summary.credit_balance : prev);
    } catch (error) {
      console.error('크레딧 정보를 불러오지 못했습니다:', error);
    } finally {
      setCreditLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isLoggedIn()) {
      refreshCreditSummary();
    }
  }, [authLoading]);

  const animateCreditBalance = (nextBalance: number) => {
    if (creditAnimationRef.current) {
      window.clearInterval(creditAnimationRef.current);
    }

    const startBalance = displayCreditBalance ?? creditSummary?.credit_balance ?? nextBalance;
    const distance = nextBalance - startBalance;
    const steps = 28;
    let currentStep = 0;

    creditAnimationRef.current = window.setInterval(() => {
      currentStep += 1;
      const progress = Math.min(1, currentStep / steps);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCreditBalance(Math.round(startBalance + distance * eased));

      if (progress >= 1 && creditAnimationRef.current) {
        window.clearInterval(creditAnimationRef.current);
        creditAnimationRef.current = null;
        setDisplayCreditBalance(nextBalance);
      }
    }, 28);
  };

  const selectedCount = useMemo(() => items.filter(item => item.selected).length, [items]);
  const validSelectedItems = useMemo(
    () => items.filter(item => item.selected && validateItem(item).length === 0),
    [items]
  );
  const hasInput = rawText.trim().length > 0 || Boolean(file);
  const canCreate = validSelectedItems.length > 0 && validSelectedItems.length === selectedCount && !creating;
  const isAiAgentProvider = provider === 'ai-agent';
  const inputTextLength = useMemo(() => Array.from(rawText || fileText).length, [rawText, fileText]);
  const isAiAgentInputTooLarge = isAiAgentProvider && inputTextLength > AI_AGENT_STABLE_MAX_CHARS;
  const aiAgentCapacityPercent = inputTextLength > 0
    ? Math.min(100, (inputTextLength / AI_AGENT_STABLE_MAX_CHARS) * 100)
    : 0;
  const canParse = hasInput && !parsing && !authLoading && isLoggedIn() && !isAiAgentInputTooLarge;
  const estimatedCreditCost = isAiAgentProvider && inputTextLength > 0
    ? Math.ceil(inputTextLength / 1000) * 100
    : 0;
  const currentCreditBalance = creditSummary?.credit_balance ?? 0;
  const isCreditInsufficient = isAiAgentProvider && Boolean(creditSummary) && estimatedCreditCost > currentCreditBalance;
  const canRequestCredit = isAiAgentProvider && Boolean(creditSummary) && (
    currentCreditBalance < (creditSummary?.low_balance_threshold || 1000) ||
    isCreditInsufficient ||
    forceCreditRequestVisible
  );
  const elapsedMinutes = Math.floor(parseElapsedSeconds / 60);
  const elapsedSeconds = String(parseElapsedSeconds % 60).padStart(2, '0');
  const parsingMessage = isAiAgentProvider
    ? 'AI Agent가 원문에서 날짜가 명확한 사건만 추출하고 있습니다.'
    : '원문에서 타임라인 후보를 추출하고 있습니다.';

  const updateItem = (clientId: string, updates: Partial<ParsedArchiveItem>) => {
    setItems(prev =>
      prev.map(item => {
        if (item.client_id !== clientId) return item;
        const next = { ...item, ...updates };
        return { ...next, errors: validateItem(next) };
      })
    );
  };

  const setAllSelected = (selected: boolean) => {
    setItems(prev => prev.map(item => ({ ...item, selected })));
  };

  const handleFile = async (nextFile: File) => {
    if (!isTextFile(nextFile)) {
      toast.error('.txt 또는 .md 파일만 사용할 수 있습니다.');
      return;
    }

    setFile(nextFile);
    try {
      const text = await nextFile.text();
      setFileText(text);
      setFilePreview(text.slice(0, 5000));
      if (!rawText.trim()) setRawText(text);
    } catch (error) {
      setFileText('');
      setFilePreview('');
      toast.error('파일 내용을 읽지 못했습니다.');
    }
  };

  const handleCreditRequest = async () => {
    try {
      setCreditRequesting(true);
      const result = await requestCredit();
      toast.success(result.message || '크레딧 요청을 보냈습니다.');
      setForceCreditRequestVisible(true);
      await refreshCreditSummary();
    } catch (error: any) {
      toast.error(error.response?.data?.error || '크레딧 요청에 실패했습니다.');
    } finally {
      setCreditRequesting(false);
    }
  };

  const handleParse = async () => {
    if (isAiAgentInputTooLarge) {
      toast.error(`AI Agent 안정 처리 용량은 최대 ${AI_AGENT_STABLE_MAX_CHARS.toLocaleString()}자입니다. 원문을 나누어 해석해 주세요.`);
      return;
    }

    if (!canParse) return;

    if (isCreditInsufficient) {
      setForceCreditRequestVisible(true);
      toast.error('크레딧이 부족합니다. 크레딧을 요청하세요');
      return;
    }

    try {
      setParsing(true);
      setDocumentWarnings([]);
      const result = await parseRawArchive({
        topicAreaId,
        rawText,
        file: rawText.trim() ? null : file,
        provider,
        maxItems
      });
      const sortedItems = [...result.items].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      setItems(sortedItems.map(item => ({ ...item, errors: validateItem(item) })));
      setDocumentWarnings(result.warnings || []);
      const credits = result.credits;
      if (credits) {
        setCreditSummary(prev => prev
          ? { ...prev, credit_balance: credits.balance }
          : {
              credit_balance: credits.balance,
              low_balance_threshold: 1000,
              pending_request: null
            }
        );
        animateCreditBalance(credits.balance);
        setForceCreditRequestVisible(credits.balance < 1000);
      }
      toast.success(`${result.items.length}건의 후보를 해석했습니다.`);
    } catch (error: any) {
      if (error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
        setForceCreditRequestVisible(true);
        if (typeof error.response.data.credit_balance === 'number') {
          setCreditSummary(prev => prev ? { ...prev, credit_balance: error.response.data.credit_balance } : prev);
          animateCreditBalance(error.response.data.credit_balance);
        }
      }
      toast.error(error.response?.data?.error || '해석 요청에 실패했습니다.');
    } finally {
      setParsing(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!canCreate) return;

    try {
      setCreating(true);
      const result = await bulkCreateRawArchive(topicAreaId, validSelectedItems);
      toast.success(`${result.created_count}건을 등록했습니다.`);
      navigate(`/?selectedTopicAreaId=${topicAreaId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || '선택 항목 등록에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  if (authLoading || pageLoading) {
    return <div className="raw-import-page">Loading...</div>;
  }

  if (!isLoggedIn()) {
    return (
      <div className="raw-import-page">
        <section className="raw-import-empty">
          <h2>접근 권한이 없습니다</h2>
          <p>비정형 기록 등록은 로그인한 사용자만 사용할 수 있습니다.</p>
        </section>
      </div>
    );
  }

  if (pageError || !topicArea) {
    return (
      <div className="raw-import-page">
        <section className="raw-import-empty">
          <h2>주제영역을 확인할 수 없습니다</h2>
          <p>{pageError || '주제영역 정보가 없습니다.'}</p>
          <Link to="/topic-areas" className="raw-import-link-button">목록으로</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="raw-import-page">
      <header className="raw-import-header">
        <div>
          <p className="raw-import-eyebrow">비정형 기록 등록</p>
          <h1>{topicArea.title}</h1>
          <p>{topicArea.summary}</p>
        </div>
        <div className="raw-import-header-actions">
          <button type="button" onClick={() => navigate(`/topic-areas/${topicAreaId}`)}>
            뒤로가기
          </button>
          <button type="button" onClick={() => navigate(`/?selectedTopicAreaId=${topicAreaId}`)}>
            타임라인 보기
          </button>
        </div>
      </header>

      <section className="raw-import-input-grid">
        <div className="raw-import-panel">
          <h2>원문 입력</h2>
          <div
            className={`raw-import-dropzone ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={event => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={event => {
              event.preventDefault();
              setIsDragOver(false);
              const dropped = event.dataTransfer.files?.[0];
              if (dropped) handleFile(dropped);
            }}
          >
            <input
              id="raw-file-input"
              type="file"
              accept=".txt,.md,text/plain,text/markdown"
              onChange={event => {
                const selectedFile = event.target.files?.[0];
                if (selectedFile) handleFile(selectedFile);
                event.target.value = '';
              }}
            />
            <label htmlFor="raw-file-input">
              {file ? file.name : '.txt 또는 .md 파일 선택'}
            </label>
            <span>파일을 놓거나 선택하면 원문 영역에 불러옵니다.</span>
          </div>
          {filePreview && (
            <details className="raw-import-preview">
              <summary>파일 미리보기</summary>
              <pre>{filePreview}</pre>
            </details>
          )}
          <textarea
            value={rawText}
            onChange={event => setRawText(event.target.value)}
            placeholder="해석할 역사 기록 원문을 붙여넣으세요."
            className="raw-import-textarea"
          />
          <div className="raw-import-text-stats">
            <span>입력 글자수</span>
            <strong>{inputTextLength.toLocaleString()}자</strong>
          </div>
        </div>

        <aside className="raw-import-panel raw-import-options">
          <h2>해석 옵션</h2>
          <label>
            Provider
            <select value={provider} onChange={event => setProvider(event.target.value)}>
              <option value="mock">mock (Pattern)</option>
              <option value="ai-agent">AI Agent</option>
              <option value="custom" disabled>Custom (Disabled)</option>
              <option value="gemini" disabled>gemini (Disabled)</option>
              <option value="ollama" disabled>ollama (Disabled)</option>
            </select>
          </label>
          {isAiAgentProvider && (
            <div className={`raw-import-credit-card ${isCreditInsufficient || isAiAgentInputTooLarge ? 'insufficient' : ''}`}>
              <div className="raw-import-credit-topline">
                <span>보유 크레딧</span>
                <strong className="raw-import-credit-balance">
                  {(displayCreditBalance ?? currentCreditBalance).toLocaleString()}C
                </strong>
              </div>
              <div className="raw-import-credit-meter">
                <span
                  style={{
                    width: `${Math.max(4, Math.min(100, ((displayCreditBalance ?? currentCreditBalance) / 5000) * 100))}%`
                  }}
                />
              </div>
              <div className="raw-import-credit-breakdown">
                <span>분석 글자수</span>
                <strong>{inputTextLength.toLocaleString()}자</strong>
              </div>
              <div className="raw-import-credit-breakdown">
                <span>AI Agent 안정 한도</span>
                <strong>{AI_AGENT_STABLE_MAX_CHARS.toLocaleString()}자</strong>
              </div>
              <div className="raw-import-ai-capacity" aria-hidden="true">
                <span style={{ width: `${Math.max(4, aiAgentCapacityPercent)}%` }} />
              </div>
              <div className="raw-import-credit-breakdown">
                <span>예상 차감</span>
                <strong>{estimatedCreditCost.toLocaleString()}C</strong>
              </div>
              <p>
                AI Agent는 서버에서 실제 텍스트 길이와 잔액을 다시 검증한 뒤 1000자당 100C를 차감합니다.
              </p>
              {isCreditInsufficient && (
                <div className="raw-import-credit-alert">
                  크레딧이 부족합니다. 크레딧을 요청하세요
                </div>
              )}
              {isAiAgentInputTooLarge && (
                <div className="raw-import-credit-alert">
                  AI Agent 안정 처리 한도를 초과했습니다. 원문을 {AI_AGENT_STABLE_MAX_CHARS.toLocaleString()}자 이하로 나누어 해석해 주세요.
                </div>
              )}
              {(canRequestCredit || creditSummary?.pending_request) && (
                <button
                  type="button"
                  className="raw-import-credit-request-button"
                  onClick={handleCreditRequest}
                  disabled={creditRequesting || creditLoading || Boolean(creditSummary?.pending_request)}
                >
                  {creditSummary?.pending_request
                    ? '크레딧 요청 대기 중'
                    : creditRequesting
                      ? '요청 중...'
                      : '크레딧(C) 요청하기'}
                </button>
              )}
            </div>
          )}
          <label>
            최대 항목 수
            <input
              type="number"
              min={1}
              max={100}
              value={maxItems}
              onChange={event => setMaxItems(Math.min(100, Math.max(1, Number(event.target.value) || 1)))}
            />
          </label>
          <button type="button" onClick={handleParse} disabled={!canParse} className="raw-import-primary-button">
            {parsing ? '해석 중...' : '해석하기'}
          </button>
          {!hasInput && <p className="raw-import-hint">파일 또는 원문 텍스트를 입력하면 해석할 수 있습니다.</p>}
        </aside>
      </section>

      {(documentWarnings.length > 0 || items.length > 0) && (
        <section className="raw-import-results">
          {documentWarnings.length > 0 && (
            <div className="raw-import-warning-box">
              {documentWarnings.map((warning, index) => (
                <span key={index}>{warning}</span>
              ))}
            </div>
          )}

          <div className="raw-import-toolbar">
            <div>
              <h2>해석 결과</h2>
              <p>선택 {selectedCount}건 / 등록 가능 {validSelectedItems.length}건</p>
            </div>
            <div className="raw-import-toolbar-actions">
              <button type="button" onClick={() => setAllSelected(true)}>전체선택</button>
              <button type="button" onClick={() => setAllSelected(false)}>전체해제</button>
              <button
                type="button"
                onClick={handleBulkCreate}
                disabled={!canCreate}
                className="raw-import-primary-button"
              >
                {creating ? '등록 중...' : '해석된 기록 선택항목 등록하기'}
              </button>
            </div>
          </div>

          <div className="raw-import-item-list">
            {items.map(item => {
              const currentErrors = validateItem(item);
              const statusClass = currentErrors.length > 0 ? 'error' : item.warnings.length > 0 ? 'warning' : 'ok';
              return (
                <article key={item.client_id} className={`raw-import-item ${statusClass}`}>
                  <div className="raw-import-item-select">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={event => updateItem(item.client_id, { selected: event.target.checked })}
                    />
                    <span>{currentErrors.length > 0 ? '오류' : item.warnings.length > 0 ? '경고' : '정상'}</span>
                  </div>

                  <div className="raw-import-item-fields">
                    <label>
                      날짜
                      <input
                        type="datetime-local"
                        value={item.timestamp}
                        onChange={event => updateItem(item.client_id, { timestamp: event.target.value })}
                      />
                    </label>
                    <label>
                      제목
                      <input
                        type="text"
                        value={item.title}
                        maxLength={220}
                        onChange={event => updateItem(item.client_id, { title: event.target.value })}
                      />
                    </label>
                    <label className="raw-import-wide-field">
                      설명
                      <textarea
                        value={item.description}
                        onChange={event => updateItem(item.client_id, { description: event.target.value })}
                      />
                    </label>
                    <label>
                      링크
                      <input
                        type="text"
                        value={item.lnks}
                        onChange={event => updateItem(item.client_id, { lnks: event.target.value })}
                      />
                    </label>
                    <label>
                      태그
                      <input
                        type="text"
                        value={item.tags.join(', ')}
                        onChange={event => updateItem(item.client_id, { tags: normalizeTags(event.target.value) })}
                      />
                    </label>
                    <div className="raw-import-wide-field">
                      <span className="raw-import-label">카테고리</span>
                      <CategoryAutocomplete
                        selectedIds={toSelectedCategoryIds(item)}
                        maxSelections={3}
                        onChange={ids =>
                          updateItem(item.client_id, {
                            main_category1_id: ids[0] || null,
                            main_category2_id: ids[1] || null,
                            main_category3_id: ids[2] || null
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="raw-import-item-meta">
                    <span>날짜 원문: {item.date_text || '-'}</span>
                    <span>정밀도: {item.date_precision}</span>
                    <span>신뢰도: {Math.round(item.confidence * 100)}%</span>
                  </div>

                  {(currentErrors.length > 0 || item.warnings.length > 0) && (
                    <div className="raw-import-chip-row">
                      {currentErrors.map((error, index) => (
                        <span key={`error-${index}`} className="raw-import-chip error">{error}</span>
                      ))}
                      {item.warnings.map((warning, index) => (
                        <span key={`warning-${index}`} className="raw-import-chip warning">{warning}</span>
                      ))}
                    </div>
                  )}

                  {item.source_excerpt && (
                    <details className="raw-import-source">
                      <summary>근거 원문</summary>
                      <p>{item.source_excerpt}</p>
                    </details>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {parsing && (
        <div className="raw-import-loading-backdrop" role="status" aria-live="polite">
          <div className="raw-import-loading-panel">
            <div className="raw-import-loading-title">
              {isAiAgentProvider ? 'AI Agent 해석 중' : '해석 중'}
            </div>
            <div className="raw-import-loading-meter" aria-hidden="true">
              <span />
            </div>
            <p>{parsingMessage}</p>
            <div className="raw-import-loading-steps" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <div className="raw-import-loading-meta">
              경과 시간 {elapsedMinutes}:{elapsedSeconds}
            </div>
            {isAiAgentProvider && (
              <div className="raw-import-loading-note">
                긴 원문은 응답까지 몇 분 정도 걸릴 수 있습니다.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RawArchiveImportPage;
